import DeleteIcon from '@mui/icons-material/Delete';
import { Autocomplete, Box, IconButton, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import ErrorText from '../../../../components/Form/ErrorText';
import {
  isUnassignedSurveyorId,
  normaliseAdditionalSurveyorIds,
  normaliseSurveyorAssignmentChange,
} from '../../../../shared/surveyorAssignment';

export type RoleLike = {
  uid: string;
  nameFirst: string;
  nameLast: string;
  email: string;
};

type AssignmentErrors = {
  main?: boolean;
};

type AssignmentChange = {
  mainSurveyorId: string;
  additionalSurveyorIds: string[];
};

type SurveyorAssignmentSelectorProps = {
  surveyors: RoleLike[];
  mainSurveyorId: string;
  additionalSurveyorIds: string[];
  onChange: (next: AssignmentChange) => void;
  maxAdditional?: number;
  mainRequired?: boolean;
  disabled?: boolean;
  errors?: AssignmentErrors;
  hideAdditional?: boolean;
};

function dedupeIds(ids: string[]) {
  return Array.from(new Set((ids || []).filter(Boolean)));
}

function SurveyorField({
  label,
  value,
  surveyors,
  excludedIds,
  onChange,
  onRemove,
  disabled,
}: {
  label: string;
  value: string;
  surveyors: RoleLike[];
  excludedIds: string[];
  onChange: (id: string) => void;
  onRemove?: () => void;
  disabled?: boolean;
}) {
  const availableSurveyors = useMemo(() => {
    const excluded = new Set(excludedIds.filter((id) => id && id !== value));
    return surveyors.filter((s) => !excluded.has(s.uid));
  }, [excludedIds, surveyors, value]);

  const selectedSurveyor = useMemo(() => {
    return value ? surveyors.find((s) => s.uid === value) || null : null;
  }, [surveyors, value]);

  const isMissing = !!value && !selectedSurveyor;
  const isUnselected = !value;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Autocomplete
          fullWidth
          disabled={disabled}
          value={selectedSurveyor}
          options={availableSurveyors}
          getOptionLabel={(s) => `${s.nameFirst} ${s.nameLast} - [${s.email}]`}
          isOptionEqualToValue={(option, val) => option.uid === val?.uid}
          onChange={(_, newValue) => onChange(newValue?.uid || '')}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              placeholder="Select a surveyor"
              error={isUnselected || isMissing}
            />
          )}
        />
        {onRemove ? (
          <IconButton
            onClick={onRemove}
            color="secondary"
            size="small"
            aria-label="Remove surveyor"
            disabled={disabled}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
      {isUnselected ? <ErrorText text="Please select a surveyor" /> : null}
      {isMissing ? (
        <ErrorText text="Selected surveyor is no longer available. Please choose another." />
      ) : null}
    </Box>
  );
}

export default function SurveyorAssignmentSelector({
  surveyors,
  mainSurveyorId,
  additionalSurveyorIds,
  onChange,
  maxAdditional = 5,
  mainRequired = true,
  disabled = false,
  errors,
  hideAdditional = false,
}: SurveyorAssignmentSelectorProps) {
  const selectedAdditionalIds = additionalSurveyorIds || [];
  const dedupedAdditionalIds = dedupeIds(selectedAdditionalIds);
  const hasUnfilledSlots = selectedAdditionalIds.some((id) => !id);
  const mainSurveyorUnassigned = isUnassignedSurveyorId(mainSurveyorId);
  const canAddMore =
    !mainSurveyorUnassigned && selectedAdditionalIds.length < maxAdditional;

  const availableMainSurveyors = useMemo(() => {
    const excludedAdditional = new Set(
      hideAdditional ? [] : dedupedAdditionalIds,
    );
    return surveyors.filter(
      (s) => !excludedAdditional.has(s.uid) || s.uid === mainSurveyorId,
    );
  }, [dedupedAdditionalIds, hideAdditional, mainSurveyorId, surveyors]);

  const selectedMain = useMemo(() => {
    return mainSurveyorId
      ? surveyors.find((s) => s.uid === mainSurveyorId) || null
      : null;
  }, [mainSurveyorId, surveyors]);
  const mainUnavailable = !!mainSurveyorId && !selectedMain;
  const mainInvalid =
    (mainRequired && !mainSurveyorId) || mainUnavailable || !!errors?.main;

  const handleMainChange = (newMainSurveyorId: string) => {
    onChange(
      normaliseSurveyorAssignmentChange({
        mainSurveyorId: newMainSurveyorId,
        additionalSurveyorIds: selectedAdditionalIds,
      }),
    );
  };

  const handleAdditionalChange = (index: number, newId: string) => {
    const newIds = [...selectedAdditionalIds];
    newIds[index] = newId;
    onChange({
      mainSurveyorId,
      additionalSurveyorIds: normaliseAdditionalSurveyorIds(
        newIds,
        mainSurveyorId,
      ),
    });
  };

  const handleRemoveAdditional = (index: number) => {
    const newIds = selectedAdditionalIds.filter((_, i) => i !== index);
    onChange({
      mainSurveyorId,
      additionalSurveyorIds: newIds,
    });
  };

  const handleAddAdditional = () => {
    if (hasUnfilledSlots || !canAddMore) {
      return;
    }
    onChange({
      mainSurveyorId,
      additionalSurveyorIds: [...selectedAdditionalIds, ''],
    });
  };

  return (
    <>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          fullWidth
          disabled={disabled}
          id="select-main-surveyor"
          options={availableMainSurveyors}
          getOptionLabel={(s) => `${s.nameFirst} ${s.nameLast} - [${s.email}]`}
          value={selectedMain}
          isOptionEqualToValue={(option, value) => option.uid === value.uid}
          onChange={(_, data) => handleMainChange(data?.uid || '')}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Main Surveyor"
              variant="outlined"
              autoComplete="off"
              error={mainInvalid}
              helperText={
                mainUnavailable
                  ? 'Selected main surveyor is no longer available.'
                  : mainInvalid
                  ? 'Main surveyor is required'
                  : ''
              }
            />
          )}
        />
      </Grid>

      {!hideAdditional
        ? selectedAdditionalIds.map((id, index) => (
            <Grid item xs={12} sm={6} key={`additional-${index}`}>
              <SurveyorField
                label={`Additional Surveyor ${index + 1}`}
                value={id}
                surveyors={surveyors}
                excludedIds={[mainSurveyorId, ...selectedAdditionalIds]}
                onChange={(newId) => handleAdditionalChange(index, newId)}
                onRemove={() => handleRemoveAdditional(index)}
                disabled={disabled}
              />
            </Grid>
          ))
        : null}

      {!hideAdditional && canAddMore ? (
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              onClick={handleAddAdditional}
              disabled={disabled || mainSurveyorUnassigned || hasUnfilledSlots}
            >
              Add Additional Surveyor ({selectedAdditionalIds.length}/
              {maxAdditional})
            </Button>
            {hasUnfilledSlots ? (
              <Typography variant="caption" color="secondary">
                Complete existing additional surveyor fields before adding more.
              </Typography>
            ) : null}
          </Box>
        </Grid>
      ) : null}
    </>
  );
}
